// Implements: F-UIX-009
/**
 * Signing out, and the one rule that makes it safe: the cache goes too.
 *
 * `POST /auth/logout` -> `204` -> the cache is emptied -> `/login`. It is the
 * WHOLE cache and not only `["session","scopes"]`, because anything cached
 * belongs to whoever has just left: a session list, a gallery, a mockup.
 * Removing the session key alone would leave the previous person's data one
 * render away from the next one. `emptySession` is what does it, and its own
 * comment explains why the order it uses is not interchangeable with
 * `queryClient.clear()`.
 *
 * A network failure ends the session here anyway. Leaving somebody on a screen
 * that believes it still has a session is the worse of the two outcomes: the
 * cookie may already be gone on the server, and the next request would answer
 * `401` and redirect regardless — only later, and from somewhere confusing.
 *
 * Ending the session locally is NOT the same as reporting that it ended, and
 * the two used to be one thing here. When the wire fails, this browser is
 * emptied but the cookie may still be valid on the server, so `signOut`
 * answers whether the server confirmed the revocation and lets the caller say
 * so. Announcing a sign-out that never reached the server is how somebody
 * walks away from a shared machine believing a live session is closed.
 */

import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router";
import type { NavigateFunction } from "react-router";

import { LOGIN_ROUTE } from "../../../shared/api/nextParam";
import { emptySession } from "../../../shared/api/session";
import { logout } from "../api/authApi";

/**
 * Forget everything this browser knew and go to `/login`.
 *
 * Exported because `useRevokeSession` needs exactly this when the row being
 * revoked is the current session: that is not a list refresh, it is a sign-out
 * that happened to start from a different button.
 */
export function endSession(queryClient: QueryClient, navigate: NavigateFunction): void {
  emptySession(queryClient);
  void navigate(LOGIN_ROUTE, { replace: true });
}

export interface UseLogoutResult {
  /**
   * Sign out. It never rejects, and only one call runs at a time.
   *
   * It answers `true` when the SERVER confirmed the revocation and `false`
   * when it did not. Either way this browser is emptied and sent to `/login`;
   * the answer is about the cookie on the other end, which `false` says may
   * still be alive.
   *
   * The answer exists rather than a failure flag for the reason
   * `useRevokeSession` gives: the closure that did the awaiting was built on
   * the previous render and would read the previous value, so a caller that
   * has only a flag ends up announcing a sign-out the server refused.
   */
  readonly signOut: () => Promise<boolean>;
  /** A request is on its way; the control that started it says so. */
  readonly pending: boolean;
}

export function useLogout(): UseLogoutResult {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  // A ref and not state, for the same reason `useLogin` uses one: `pending` is
  // only observable after a render, so two clicks in the same tick would both
  // read `false` and both send a request. It holds the running attempt and not
  // a flag, so the second click can answer with the first one's outcome: a
  // press that sent nothing must not invent a `false` and raise a failure
  // notice over a sign-out that in fact worked.
  const inFlight = useRef<Promise<boolean> | null>(null);

  const signOut = useCallback((): Promise<boolean> => {
    if (inFlight.current !== null) return inFlight.current;
    setPending(true);

    const attempt = (async (): Promise<boolean> => {
      try {
        await logout();
        return true;
      } catch {
        // The ERROR is deliberately swallowed — see the file comment, the
        // session ends here whatever the wire said. The OUTCOME is not: it
        // leaves as `false` so the caller can tell somebody.
        return false;
      } finally {
        inFlight.current = null;
        setPending(false);
        endSession(queryClient, navigate);
      }
    })();

    inFlight.current = attempt;
    return attempt;
  }, [queryClient, navigate]);

  return { signOut, pending };
}
